import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Formik, Form, FormikHelpers } from 'formik';
import { observer, inject } from 'mobx-react';
import merge from 'lodash.merge';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';

import compose from 'helpers/compose';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import Info from 'components/Tooltip/Info';
import CustomPaper from 'components/Layout/Background';
import Switch from 'components/FormFields/Switch';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import ControlButtons from 'components/ControlButtons';
import Message, { MessageVariant } from 'components/Message/Message';
import { sqlDateToLabel } from 'helpers/helpers';

import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { WithdrawMoneyStore } from 'stores/account/withdrawMoney/withdrawMoneyStore';
import { Withdrawal, initialValues } from 'stores/account/withdrawMoney/model';
import { RootStore } from 'stores/rootStore';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { AccountStatus, AccountMode } from 'enums';

interface Props {
  rootStore: RootStore;
  accountDetailStore: AccountDetailStore;
}

const Withdraw: React.FC<Props> = ({ rootStore, accountDetailStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const [store] = useState(new WithdrawMoneyStore(rootStore, accountDetailStore, accountId));
  const { fetchWithdrawalData, data } = store;
  const { currency } = accountDetailStore;
  const readOnly = !accountDetailStore.editMode;

  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [currency, t]);

  useEffect(() => {
    fetchWithdrawalData();
  }, [accountId, fetchWithdrawalData]);

  const handleSubmit = (values: Withdrawal, formikHelpers: FormikHelpers<Withdrawal>) => {
    store.handleNext(values, formikHelpers);
  };

  initialValues.amount.currency = currency;

  return data ? (
    <Formik initialValues={merge({}, initialValues, data)} onSubmit={handleSubmit}>
      {({ values, isSubmitting }) => {
        return (
          <Form id="money-withdrawal">
            <Container maxWidth="md">
              <CustomPaper>
                <Grid container>
                  <Grid item xs={6}>
                    <Switch
                      name="withdrawAll"
                      label={t('accountDetail_moneyWithdrawal_withdrawAll')}
                      disabled={readOnly || values.ltipAllowed === false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Switch
                      fast={false}
                      name="closeAccount"
                      disabled={!values.withdrawAll || readOnly || values.ltipAllowed === false}
                      label={t('accountDetail_moneyWithdrawal_closeAccount')}
                    />
                  </Grid>
                  <GridSubtitle typographyProps={{ variant: 'h3' }}>
                    {t('accountDetail_withdraw')} <Info content={t('tooltip_moneyWithdrawal_withdrawMoney')} />
                  </GridSubtitle>

                  <Grid item xs={12} sm={6}>
                    <FormNumberField
                      fast={false}
                      label={t('accountDetail_moneyWithdrawal_amount')}
                      name="amount.value"
                      disabled={!!values.withdrawAll || readOnly}
                      endAdornment={translatedCurrency}
                      tooltipContent={t('tooltip_moneyWithdrawal_amountToWithdraw')}
                      specificError
                    />
                    <TextField
                      name={`amount.currency`}
                      type="hidden"
                      style={{ visibility: 'hidden' }}
                      value={currency}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormSelectField
                      name="selectedBankAccount"
                      label={
                        values.options?.bankAccountOptions.length === 0
                          ? t('accountDetail_moneyWithdrawal_noAccounts')
                          : t('general_bankAccount_number')
                      }
                      options={values.options!.bankAccountOptions.map((account) => ({
                        value: account.id,
                        label: account.number,
                      }))}
                      disabled={readOnly || values.options?.bankAccountOptions.length === 0}
                      tooltipContent={t('tooltip_moneyWithdrawal_bankAccount')}
                    />
                  </Grid>
                </Grid>

                {accountDetailStore.mode === AccountMode.LTIP &&
                  values.minStart &&
                  accountDetailStore.status !== AccountStatus.AWAITING_APPROVAL && (
                    <Message
                      id={`LTIP_minStart`}
                      message={t('accountDetail_moneyWithdrawal_minStart', {
                        minStart: sqlDateToLabel(values.minStart, 'dd. MM. yyyy'),
                      })}
                      variant={MessageVariant.WARNING}
                      permanent
                    />
                  )}
              </CustomPaper>
            </Container>

            <ControlButtons
              isSubmitting={isSubmitting}
              hideBack={true}
              nextText={t(accountDetailStore.nextStepLabel)}
              nextDisabled={values.options?.bankAccountOptions.length === 0}
            />
          </Form>
        );
      }}
    </Formik>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Withdraw);
